import {AfterViewInit, Component, OnInit, inject} from '@angular/core';
import {Subject, of} from 'rxjs';
import {empty} from '../../functions';
import {catchError} from 'rxjs/operators';
import {JobService} from '../../shared/services/job.service';
import {ModalService} from '../../shared/services/modal.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {EducationService} from '../../shared/services/education.service';
import {AdminService} from '../../shared/services/admin.service';
import {InterestService} from '../../shared/services/interest.service';
import {DomainService} from '../../shared/services/domain.service';
import {AlertService} from '../../shared/services/alert.service';
import {FormDialogService} from '../../shared/forms/form-dialog.service';
import {
  buildJobPostingDefinition,
  saveJobPosting,
  type JobPostingModel,
} from '../../shared/entities/job-posting/job-posting';
import {JobPostingService} from '../../api/services/job-posting.service';
import type {JobPosting} from '../../api/models/job-posting';

@Component({
    selector: 'app-admin-jobs',
    templateUrl: './admin-jobs.component.html',
    styleUrls: ['./admin-jobs.component.scss'],
    standalone: false
})
export class AdminJobsComponent implements OnInit, AfterViewInit {

  filterOpen = false;

  public view = {
    filters: {
      search: '',
      city: '',
      radius: '',
      areaOfInterests: <string[]>[],
      educationareaOfInterests: [],
      lookingFor: '',
      sites: <any>[],
      educationLevels: [],
      queryParams: {
        SearchTerm: null,
        Location: null,
        AreaOfInterests: [],
        MaximumDistance: null,
        EducationLevels: [],
        SortField: 'createdOn',
        SortType: 'Descending',
      }
    },
    filterOptions: {
      areaOfInterests: <any>[],
      sites: <any>[],
      status: <any>[],
      educationLevels: <any>[],
    },

  };

  configTable = {
    hasPagination: true,
    columns: [
      {
        header_name: 'Title',
        name: 'title',
        type: 'text'
      },
      {
        header_name: 'Company name',
        name: 'company',
        subName: 'name',
        type: 'text'
      },
      {
        header_name: 'Status',
        name: 'status',
        type: 'text'
      },
    ],
    endpoint: 'jobposting/all',
    data: <any> [],
    buttons: [
      {
        class: 'btn btn-info',
        event: 'profile',
        text: 'Profile'
      },
      {
        class: 'btn btn-danger',
        event: 'delete',
        text: 'Delete'
      },
      {
        class: 'btn btn-warning',
        event: 'edit',
        text: 'Edit'
      }
    ],
    class: '',
    reloadListener: new Subject(),
    queryParams: this.view.filters.queryParams
  };

  private readonly formDialog = inject(FormDialogService);
  private readonly alert = inject(AlertService);
  private readonly jobPostingApi = inject(JobPostingService);

  constructor(public jobService: JobService,
              public educationService: EducationService,
              public adminService: AdminService,
              public interest: InterestService,
              public domain: DomainService,
              public modal: ModalService,
              private router: Router,
              private route: ActivatedRoute) { }

  /**
   * Ng on init
   */
  ngOnInit(): void {

    this.view.filterOptions.educationLevels = [];
    this.educationService.apiGetEducationLevels().pipe(catchError(this.adminService.handleError('apiGetEducationLevels', false))).subscribe((result: any) => {
      if (result) {
        // Reassign a NEW array (not push) so the <app-filter-panel> signal input() updates.
        this.view.filterOptions.educationLevels = result.data.map((educationLevel: any) => ({value: educationLevel.name, label: educationLevel.name}));
      }
    });
    this.view.filterOptions.status = this.jobService.getStatuses();

    // Set area of interest filter options
    this.interest.apiGetAreaOfInterests().pipe(catchError(this.adminService.handleError('apiGetAreaOfInterests', false))).subscribe((result: any) => {
      if (!empty(result)) {
        // Reassign a NEW array (not push) so the <app-filter-panel> signal input() updates.
        this.view.filterOptions.areaOfInterests = result.data.map((item: any) => ({value: item.name, label: item.name}));
      }
    });

    // Set site filter options
    const sites = this.domain.getAllDomains();
    if (!empty(sites)) {

      sites.map((item: any) => {
        this.view.filterOptions.sites.push({value: item.id, label: item.url + ' (' + item.code + ')'});
      });

    }


  }

  ngAfterViewInit() {

    this.route.params.subscribe( (params: Params) => {

      if (params.id) {
        this.updateJobposting(params);
      }

    });

  }

  /**
   * Add job posting
   */
  addJobposting() {
    this.formDialog
      .open<JobPostingModel>(() => buildJobPostingDefinition({ entity: undefined, isAdminMode: true }))
      .subscribe(model => {
        if (!model) return;
        saveJobPosting(this.jobPostingApi, this.alert, model)
          .pipe(catchError(() => of(null)))
          .subscribe(saved => { if (saved) this.configTable.reloadListener.next(true); });
      });
  }

  /**
   * Update a company
   *
   * @param $data
   */
  updateJobposting($data: any) {
    if (empty($data['id'])) return;
    this.jobService.apiGetJobPosting($data['id'])
      .pipe(catchError(this.jobService.handleError('apiGetJobPosting', null)))
      .subscribe(posting => {
        if (!posting) return;
        this.formDialog
          .open<JobPostingModel>(() => buildJobPostingDefinition({
            entity: posting as JobPosting,
            isAdminMode: true,
          }))
          .subscribe(model => {
            if (!model) return;
            saveJobPosting(this.jobPostingApi, this.alert, model)
              .pipe(catchError(() => of(null)))
              .subscribe(saved => { if (saved) this.configTable.reloadListener.next(true); });
          });
      });
  }

  /**
   * Delete the opened company
   *
   * @param id
   */
  deleteJobposting(id:string) {

    this.modal.Confirm('Delete', 'Are you sure you want to delete this?').then( (confirm) => {
      if (confirm === true) {

        this.jobService.apiDeleteJobPosting(id).subscribe( (result) => {

          // Reload data
          this.configTable.reloadListener.next(true);

        });

      }
    }).catch( (error) => {

    });

  }

  /**
   * Open company profile (edit) page
   *
   * @param $id
   */
  openJobpostingprofile($id: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/jobs/' + $id]));
    window.open(url, '_blank');
  }

  /**
   * Handle button event of table
   *
   * @param $event
   */
  handleButtonEvent($event:any) {

    if (!empty($event['event'])) {

      switch ($event['event']) {

        case 'edit':
          this.updateJobposting($event['data']);
          break;

        case 'delete':
          this.deleteJobposting($event['data']['id']);
          break;

        case 'profile':
          this.openJobpostingprofile($event['data']['id']);
          break;
      }
    }

  }

  reloadData() {

    this.setQueryParams();

    this.configTable.reloadListener.next(true);
  }

  setQueryParams() {
    this.configTable.queryParams = this.view.filters.queryParams
  }

  resetFilters() {

    this.view.filters = {
      search: '',
      city: '',
      radius: '',
      areaOfInterests: <string[]>[],
      educationareaOfInterests: [],
      lookingFor: '',
      sites: <any>[],
      educationLevels: [],
      queryParams: {
        SearchTerm: null,
        Location: null,
        AreaOfInterests: [],
        MaximumDistance: null,
        EducationLevels: [],
        SortField: 'createdOn',
        SortType: 'Descending',
      }
    }

    this.reloadData();
  }

  applyFilters()
  {
    this.reloadData();
  }

}
